import type { HubConnection } from "@microsoft/signalr";

import { latestThrottle } from "@/shared/lib/latest-throttle";
import { checkHubPayload } from "@/shared/lib/signalr/hub-contract";
import {
  matchLiveEventSchemas,
  type MatchLiveEventName,
  type MatchLiveEventPayloads,
} from "@/shared/lib/signalr/match-live-schemas";

/**
 * Подписка на live-данные матча через hub `/hubs/match-live`.
 *
 * Возвращает СИНХРОННУЮ функцию отписки, безопасную к race на unmount
 * (KAN-43): если компонент размонтируется/сменит matchId, пока `start()`
 * ещё в полёте, отписка гарантированно остановит соединение — как через
 * флаг `cancelled` (не даём достартовать/присоединиться), так и через
 * захваченную ссылку на `connection` (останавливаем уже открытое).
 *
 * Раньше каждый из трёх потребителей (match-stats-modal, live-game-overlay,
 * match-live-board) присваивал локальный `cleanup` ТОЛЬКО после `await start()`,
 * поэтому teardown до завершения инициализации был no-op → орфан-соединение +
 * незакрытое членство в hub-группе.
 *
 * @param hubBaseUrl базовый URL API (без `/hubs/...`), например `apiUrl`.
 * @param matchId    id матча для JoinMatch/LeaveMatch.
 * @param onData     обработчик события `MatchLiveData` (фильтрацию по matchId
 *                   делает вызывающий — payload может относиться к другому матчу).
 */
/**
 * KAN-982: до 10 Tauri одного матча шлют данные раз в секунду, и бэкенд
 * рассылает каждый POST (LcuResultController отсекает только снимки со
 * СТАРШИМ временем игры, одинаковые проходят) — до 10 почти одинаковых
 * MatchLiveData в секунду, и столько же перерисовок доски целиком. Каждое
 * сообщение — полный снимок, поэтому потребителю отдаётся последний, не чаще
 * раза в это окно.
 */
export const MATCH_LIVE_COALESCE_MS = 250;

export function subscribeToMatch<T>(
  hubBaseUrl: string,
  matchId: string,
  onData: (payload: T) => void,
): () => void {
  let cancelled = false;
  let connection: HubConnection | undefined;
  const throttled = latestThrottle(onData, MATCH_LIVE_COALESCE_MS);
  const onMatchLiveData = (payload: T) => throttled.push(payload);

  void (async () => {
    try {
      const signalR = await import("@microsoft/signalr");
      if (cancelled) return;

      const hubUrl = `${hubBaseUrl.replace(/\/$/, "")}/hubs/match-live`;
      connection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl)
        .withAutomaticReconnect()
        .build();

      connection.on("MatchLiveData", onMatchLiveData as (...args: unknown[]) => void);
      // KAN-985: членство в группе после переподключения само не восстанавливается
      // (новый ConnectionId) — без повторного JoinMatch данные матча больше не
      // приходят. Поток живой: следующее сообщение принесёт свежее состояние.
      connection.onreconnected(() => {
        if (!cancelled) void connection?.invoke("JoinMatch", matchId).catch(() => {});
      });

      await connection.start();
      // Успели отписаться, пока шло negotiation — закрываем и выходим,
      // не присоединяясь к группе.
      if (cancelled) {
        void connection.stop().catch(() => {});
        return;
      }

      await connection.invoke("JoinMatch", matchId);
      // Отписались между start() и JoinMatch — корректно покидаем группу.
      if (cancelled) {
        connection
          .invoke("LeaveMatch", matchId)
          .catch(() => {})
          .finally(() => void connection?.stop().catch(() => {}));
      }
    } catch {
      // SignalR опционален; сюда же попадает abort start() при отписке.
    }
  })();

  return () => {
    cancelled = true;
    throttled.cancel();
    const c = connection;
    if (!c) return; // соединение ещё не создано — флаг cancelled не даст ему достартовать
    c.off("MatchLiveData", onMatchLiveData as (...args: unknown[]) => void);
    // stop() безопасен в любом состоянии; LeaveMatch — best-effort (reject,
    // если ещё не Connected, гасится .catch, затем всё равно stop()).
    c.invoke("LeaveMatch", matchId)
      .catch(() => {})
      .finally(() => void c.stop().catch(() => {}));
  };
}

/**
 * События матча, которые бэкенд шлёт в группу `match-live:{matchId}`.
 *
 * Имена событий и типы payload выведены из схем (`match-live-schemas`), а не
 * написаны руками: неизвестное имя события здесь не скомпилируется, а payload
 * в обработчике типизирован тем, что реально шлёт бэкенд (KAN-988, KAN-989).
 */
export type MatchLiveHandlers = {
  [K in MatchLiveEventName]?: (payload: MatchLiveEventPayloads[K]) => void;
};

/**
 * Проверка формы сообщения перед передачей в обработчик — режим предупреждения:
 * несовпадение уходит в GlitchTip, обработчик всё равно получает payload.
 * Отчёт шёл через handleSilentError, который в GlitchTip не отправляет ничего
 * (KAN-1375) — теперь через общий checkHubPayload.
 */
function reportInvalidPayload(event: MatchLiveEventName, payload: unknown) {
  checkHubPayload("match-live", event, matchLiveEventSchemas[event], payload);
}

/**
 * Подписка на события СРАЗУ НЕСКОЛЬКИХ матчей одним соединением.
 *
 * KAN-935, шаг 2. Бэкенд давно шлёт `MatchStarted` / `MatchScoreUpdated` /
 * `MatchCompleted` / `VetoStateUpdated` (`GoodLuck.Api/Services/MatchLiveService.cs`),
 * но фронт слушал ровно одно событие — `MatchLiveData`, — и сетка ждала
 * следующего тика опроса. Здесь эти события наконец подбираются.
 *
 * Почему не `subscribeToMatch` в цикле: она поднимает ОТДЕЛЬНОЕ соединение на
 * каждый матч. Сетка на 16 команд — это 15+ матчей, то есть 15+ веб-сокетов
 * с одной вкладки. Группы в SignalR дешёвые, соединения — нет, поэтому здесь
 * одно соединение и много `JoinMatch`.
 *
 * Хаб под `[Authorize]`: у анонима negotiate вернёт 401, ошибка утонет в catch.
 * Вызывающий обязан гейтить подписку по логину — бесполезный negotiate на
 * каждого зрителя со стрима это ровно та нагрузка, ради которой всё затевалось.
 *
 * @param options.onReconnected вызывается после переподключения и повторного
 *   JoinMatch: события, прошедшие во время обрыва, не придут — вызывающий
 *   дозапрашивает своё состояние (KAN-985).
 * @param options.onLiveChange true — соединение поднято и группы собраны, пуш
 *   идёт; false — обрыв или закрытие. По нему вызывающий решает, нужен ли ему
 *   частый страховочный опрос (KAN-983): пока пуш жив, опрос его только дублирует.
 * @returns синхронная отписка, безопасная к race на unmount (как в `subscribeToMatch`).
 */
export function subscribeToMatches(
  hubBaseUrl: string,
  matchIds: string[],
  handlers: MatchLiveHandlers,
  options: { onReconnected?: () => void; onLiveChange?: (live: boolean) => void } = {},
): () => void {
  let cancelled = false;
  let connection: HubConnection | undefined;
  const joined = [...matchIds];
  const wrappedHandlers = new Map<MatchLiveEventName, (payload: unknown) => void>();

  if (joined.length === 0) return () => {};

  void (async () => {
    try {
      const signalR = await import("@microsoft/signalr");
      if (cancelled) return;

      const hubUrl = `${hubBaseUrl.replace(/\/$/, "")}/hubs/match-live`;
      connection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl)
        .withAutomaticReconnect()
        .build();

      for (const [event, handler] of Object.entries(handlers)) {
        if (!handler) continue;
        const name = event as MatchLiveEventName;
        // Обёртка нужна, чтобы проверить форму до вызова обработчика; отписка
        // ниже снимает именно её, поэтому ссылки хранятся рядом с подпиской.
        const wrapped = (payload: unknown) => {
          reportInvalidPayload(name, payload);
          (handler as (p: unknown) => void)(payload);
        };
        wrappedHandlers.set(name, wrapped);
        connection.on(event, wrapped as (...args: unknown[]) => void);
      }

      // Реконнект теряет членство в группах (сервер о них не помнит) — без
      // повторного JoinMatch соединение живо, а события не приходят. Это ровно
      // тот класс «молчаливой потери», из-за которого страховочный опрос и
      // оставлен, но чинить его всё равно надо здесь, а не там.
      connection.onreconnecting(() => {
        if (!cancelled) options.onLiveChange?.(false);
      });
      connection.onclose(() => {
        if (!cancelled) options.onLiveChange?.(false);
      });
      connection.onreconnected(() => {
        const c = connection;
        if (!c || cancelled) return;
        void Promise.all(joined.map((id) => c.invoke("JoinMatch", id)))
          .then(() => { if (!cancelled) options.onLiveChange?.(true); })
          .catch(() => {});
        options.onReconnected?.();
      });

      await connection.start();
      if (cancelled) {
        void connection.stop().catch(() => {});
        return;
      }

      let allJoined = true;
      for (const id of joined) {
        if (cancelled) break;
        await connection.invoke("JoinMatch", id).catch(() => { allJoined = false; });
      }
      if (!cancelled && allJoined) options.onLiveChange?.(true);
    } catch {
      // SignalR опционален: у анонима хаб под [Authorize] отдаст 401, и это
      // штатный сценарий — страховочный опрос сетки остаётся источником правды.
    }
  })();

  return () => {
    cancelled = true;
    const c = connection;
    if (!c) return;
    for (const [event, wrapped] of wrappedHandlers) {
      c.off(event, wrapped as (...args: unknown[]) => void);
    }
    void c.stop().catch(() => {});
  };
}
